import { memo, useState } from "react";
import StatCardRow from "../../shared/StatCardRow";
import RecordModal from "../../shared/RecordModal";
import DashboardTable from "../../shared/DashboardTable";
import InfoBanner from "../../shared/InfoBanner";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { useDebounce } from "../../../../utils/useDebounceSearch";
import { fetchUsers } from "../../services/dashboardApi";
import { withId } from "../../utils/drillHelpers";
import "../../reportDashboard.scss";

const TIER_TONES = { 1: "kudos", 2: "accent", 3: "muted", 4: "action" };

const centeredCount = { type: "number", align: "center", headerAlign: "center" };

const credTone = (u) => {
  if (u.credExpired > 0) return "danger";
  if (u.credCritical > 0) return "warning";
  if (u.credHigh > 0) return "corrective";
  return "muted";
};

const engagementTone = (score) => {
  if (score == null) return "muted";
  if (score >= 75) return "kudos";
  if (score >= 50) return "warning";
  if (score >= 30) return "corrective";
  return "danger";
};

const UsersSection = ({ data, apiCtx }) => {
  // Roster aggregates come off kpis, not a full roster array. The page payload used to carry
  // every user row (each with its own nested engagementParts list) purely so this section could
  // reduce them to the four numbers below — while the table itself has always paged through
  // /UsersList. Same four numbers, computed once server-side over the same roster.
  const { kpis } = data;
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const roster = usePaginatedList(
    (params) => fetchUsers(apiCtx, { ...params, search: debouncedSearch }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to, debouncedSearch],
  );

  // HTML (renderUsers, line 2774) leaves avgEngagement null — not 0 — when no
  // user has a score yet, so the stat card falls back to "—" instead of "0%".
  // The API preserves that distinction: avgEngagementScore is nullable there too.
  const avgEngagement = kpis.avgEngagementScore ?? null;
  const lowerEngagement = kpis.lowEngagementUsers || 0;
  const credentialAlerts = kpis.usersWithCredentialAlerts || 0;

  const columns = [
    { field: "userName", headerName: "Name", flex: 1, minWidth: 110 },
    { field: "role", headerName: "Role", flex: 1, minWidth: 95, valueFormatter: ({ value }) => value || "—" },
    {
      field: "tierLabel",
      headerName: "Access Tier",
      width: 150,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => (
        <span className={`rdPill rdPill--tinted is-${TIER_TONES[params.row.tier] || "muted"}`}>
          {params.value}
        </span>
      ),
    },
    { field: "kudos", headerName: "Kudos", width: 80, ...centeredCount },
    { field: "corrective", headerName: "Corrective", width: 100, ...centeredCount },
    {
      field: "credentials",
      headerName: "Credentials",
      width: 110,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => (
        <span className={`rdValue is-${credTone(params.row)}`}>{params.value}</span>
      ),
    },
    {
      field: "trainingPct",
      headerName: "Training",
      width: 100,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => (
        <span
          className="rdValue is-accent"
          title={params.value == null ? "Not applicable to this access tier" : `${params.row.trainingCompleted} completed`}
        >
          {params.value == null ? "—" : `${Math.round(params.value)}%`}
        </span>
      ),
    },
    {
      field: "engagementScore",
      headerName: "Engagement",
      width: 110,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => (
        <span className={`rdValue is-${engagementTone(params.value)}`} style={{ fontWeight: 700 }}>
          {params.value == null ? "—" : `${params.value}%`}
        </span>
      ),
    },
  ];

  const openDetail = (u) => setDetail(u);

  return (
    <div>
      <StatCardRow
        items={[
          { label: "Total Users", value: kpis.totalUsers || 0, tone: "accent" },
          {
            label: "Average Engagement",
            value: avgEngagement == null ? "—" : `${avgEngagement}%`,
            tone: "kudos",
          },
          { label: "Lower Engagement (<50%)", value: lowerEngagement, tone: "warning" },
          { label: "Credential Alerts", value: credentialAlerts, tone: "danger" },
        ]}
        columns={4}
      />

      <InfoBanner>
        About Access Tiers: Operational Lead — Inspector, Supervisor, Division
        Admin, Client System Admin. Support / Field — Field User, Maintenance
        Manager, OSHA Management. Tagged Only — Non-Privileged. Pure Oversight
        — Corporate Manager.
      </InfoBanner>
      <InfoBanner>
        About Engagement Score: a category only counts toward someone&apos;s
        score if their role makes that category possible for them — for
        example, a Non-Privileged user isn&apos;t scored on training
        completion because lessons and courses are never assigned to them
        directly, but they are still scored on safety meeting participation
        and credentials. Scores reflect activity captured in the system to
        date, not a full performance review. Open a user&apos;s profile to
        see exactly which categories applied and why.
      </InfoBanner>

      <input
        className="rdSearchInput"
        type="text"
        placeholder="Filter by name or role..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="rdSectionLabel rdSectionLabel--first">
        {roster.totalCount > 0
          ? "All Users — click a user for their full profile"
          : "All Users"}
      </div>
      <div className="rdCard">
        <div className="rdTableWrap">
          <div className="rdCard__title rdCard__title--bar">
            <span className="rdCard__titleBar">Employee Roster</span>
            <span className="rdCard__note">{roster.totalCount} users</span>
          </div>
          <DashboardTable
            columns={columns}
            rows={withId(roster.rows, "userId")}
            onRowClick={(params) => openDetail(params.row)}
            serverMode
            rowCount={roster.totalCount}
            page={roster.page}
            onPageChange={roster.setPage}
            loading={roster.loading}
            pageSize={roster.pageSize}
          />
        </div>
      </div>

      {detail && (
        <RecordModal
          open
          onClose={() => setDetail(null)}
          title={`${detail.userName} — User Profile`}
          fields={[
            { label: "Role", value: detail.role || "—" },
            { label: "Access Tier", value: detail.tierLabel },
          ]}
          sections={[
            {
              // HTML's Overall score is its own big modal-row (showUserProfile line
              // 2899-2902), the part breakdown (line 2835) is a separate, untitled
              // legend-row block right underneath it — kept as two sections here.
              title: "Engagement Score",
              type: "fields",
              fields: [
                {
                  label: "Overall",
                  value: detail.engagementScore == null ? "Not enough activity data yet" : `${detail.engagementScore}%`,
                },
              ],
            },
            {
              type: detail.engagementParts.length > 0 ? "legendRows" : "note",
              rows: detail.engagementParts.map((p) => ({
                label: p.label,
                value: `${p.score}%`,
                tone: engagementTone(p.score),
              })),
              text: "No categories apply yet — either this role does not make them possible, or there is not enough activity data on record.",
            },
            {
              type: "note",
              text: "Only counts categories this role makes possible — see the Users tab disclaimer for details.",
            },
            {
              title: "Safety Culture",
              type: detail.kudos + detail.corrective + detail.reprimands > 0 ? "legendRows" : "note",
              rows: [
                { label: "Kudos (as Involved Party)", value: detail.kudos, tone: "kudos" },
                { label: "Corrective (as Involved Party)", value: detail.corrective, tone: "danger" },
                { label: "Reprimands", value: detail.reprimands, tone: "warning" },
              ],
              text: "No Kudos, Corrective, or Reprimand items where this person is the Involved Party.",
            },
            {
              title: "Credentials",
              type: detail.credentials > 0 ? "legendRows" : "note",
              rows: [
                { label: "Total Credentials", value: detail.credentials },
                { label: "Expired", value: detail.credExpired, tone: "danger" },
                { label: "Critical (≤30d)", value: detail.credCritical, tone: "warning" },
                { label: "High (≤90d)", value: detail.credHigh, tone: "corrective" },
              ],
              text: "No credentials on file for this person.",
            },
            {
              title: "Safety Meetings",
              type: detail.meetingsInstructed + detail.meetingsAttended > 0 ? "legendRows" : "note",
              rows: [
                { label: "Instructed", value: detail.meetingsInstructed, tone: "accent" },
                {
                  label: "Attended (not instructor)",
                  value: detail.meetingsAttended,
                  tone: "kudos",
                  note: `out of ${detail.totalMeetings} safety meeting${detail.totalMeetings === 1 ? "" : "s"} held`,
                },
              ],
              text: "No Safety Meeting activity on record.",
            },
            detail.tier === 3
              ? {
                  title: "Training (Lessons & Courses)",
                  type: "note",
                  text: "Non-Privileged users are not assigned lessons or courses directly — tracked via Safety Meeting participation above instead.",
                }
              : {
                  // v26 renders this block as legend rows with three states —
                  // Completed / Assigned (not yet due) / Not Completed (overdue).
                  // LmsLessonUserMapping/LmsCourseUserMapping carry no due-date
                  // column, so "not yet due" has no real backing field here; the
                  // two states the schema can actually support are shown instead
                  // of fabricating the split (see LmsAssignmentDto's comment).
                  title: "Training (Lessons & Courses)",
                  type:
                    detail.trainingCompleted + detail.trainingNotCompleted > 0
                      ? "legendRows"
                      : "note",
                  rows: [
                    {
                      label: "Completed",
                      value: detail.trainingCompleted,
                      tone: "kudos",
                    },
                    {
                      label: "Not Completed (overdue)",
                      value: detail.trainingNotCompleted,
                      tone: "danger",
                    },
                  ],
                  text: "No lessons or courses assigned on record.",
                },
          ]}
        />
      )}
    </div>
  );
};

export default memo(UsersSection);
